import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Brain, CheckCircle2, Loader2, ShieldCheck, AlertTriangle, History } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const PRODUCT_OPTIONS = [
  { value: "final_expense", label: "Final Expense" },
  { value: "ib_term", label: "InstaBrain Term" },
  { value: "pure_term", label: "Pure Term" },
];

const GENDERS = [
  { value: "1", label: "Male" },
  { value: "2", label: "Female" },
  { value: "3", label: "Unknown / Not recorded" },
];

function productFromLead(lead) {
  const goal = `${lead?.product_goal || ""} ${lead?.quote_product || ""}`.toLowerCase();
  if (goal.includes("final") || goal.includes("expense")) return "final_expense";
  if (goal.includes("pure") && goal.includes("term")) return "pure_term";
  return "ib_term";
}

function splitName(lead) {
  const explicitFirst = String(lead?.client_first_name || "").trim();
  const explicitLast = String(lead?.client_last_name || "").trim();
  if (explicitFirst || explicitLast) return [explicitFirst, explicitLast];
  const bits = String(lead?.client_name || "").trim().split(/\s+/).filter(Boolean);
  if (bits.length <= 1) return [bits[0] || "", ""];
  return [bits[0], bits.slice(1).join(" ")];
}

function phone10(lead) {
  const digits = String(lead?.phone_digits || lead?.phone || "").replace(/\D/g, "");
  return digits.length >= 10 ? digits.slice(-10) : digits;
}

function fmtMoney(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { style: "currency", currency: "USD" }) : (value || "—");
}

function initialForm(lead) {
  const [firstName, lastName] = splitName(lead);
  return {
    product: productFromLead(lead),
    faceAmount: String(lead?.target_face_amount || lead?.quoted_face_amount || ""),
    benefitPeriod: "20",
    flagTobaccoUse: false,
    gender: String(lead?.gender_code || lead?.gender || "3"),
    stateCode: String(lead?.state || "").toUpperCase(),
    zipCode: String(lead?.zip || lead?.zipcode || ""),
    rateClass: "Standard",
    age: String(lead?.age || ""),
    dateOfBirth: String(lead?.date_of_birth || lead?.dob || "").slice(0, 10),
    paymentFreq: "Monthly",
    firstName,
    lastName,
    email: String(lead?.email || ""),
    phoneNum: phone10(lead),
  };
}

export function InstaBrainPanel({ lead, onClose, onSaved }) {
  const [form, setForm] = useState(() => initialForm(lead));
  const [status, setStatus] = useState(null);
  const [verified, setVerified] = useState(false);
  const [history, setHistory] = useState([]);
  const [result, setResult] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [quoting, setQuoting] = useState(false);

  const set = (key, value) => setForm((s) => ({ ...s, [key]: value }));
  const isTerm = form.product === "ib_term" || form.product === "pure_term";

  const selectedProductLabel = useMemo(
    () => PRODUCT_OPTIONS.find((p) => p.value === form.product)?.label || form.product,
    [form.product],
  );

  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/instabrain/quotes/${lead.id}`);
      setHistory(data || []);
    } catch {
      setHistory([]);
    }
  };

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      api.get("/instabrain/status"),
      api.get(`/instabrain/quotes/${lead.id}`),
    ]).then(([statusRes, historyRes]) => {
      if (!alive) return;
      if (statusRes.status === "fulfilled") setStatus(statusRes.value.data);
      if (historyRes.status === "fulfilled") setHistory(historyRes.value.data || []);
    }).finally(() => { if (alive) setLoadingStatus(false); });
    return () => { alive = false; };
  }, [lead.id]);

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/instabrain/verify");
      setVerified(!!data?.authenticated);
      toast.success(`InstaBrain ${data?.environment || "sandbox"} connection verified`);
    } catch (e) {
      setVerified(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "InstaBrain authentication failed");
    } finally {
      setVerifying(false);
    }
  };

  const runQuote = async () => {
    const faceAmount = Number(form.faceAmount);
    const age = form.age ? Number(form.age) : null;
    if (!Number.isFinite(faceAmount) || faceAmount <= 0) {
      toast.error("Enter a valid face amount");
      return;
    }
    if (!form.stateCode && !form.zipCode) {
      toast.error("State or ZIP code is required");
      return;
    }
    if (!form.dateOfBirth && (!Number.isFinite(age) || age <= 0)) {
      toast.error("Age or date of birth is required");
      return;
    }

    const payload = {
      faceAmount,
      flagTobaccoUse: !!form.flagTobaccoUse,
      gender: Number(form.gender) || 3,
      stateCode: form.stateCode || undefined,
      zipCode: form.zipCode || undefined,
      age: form.dateOfBirth ? undefined : age,
      dateOfBirth: form.dateOfBirth || undefined,
      paymentFreq: form.paymentFreq,
      firstName: form.firstName || undefined,
      lastName: form.lastName || undefined,
      email: form.email || undefined,
      phoneNum: form.phoneNum || undefined,
    };
    if (isTerm) {
      payload.benefitPeriod = Number(form.benefitPeriod);
      payload.rateClass = form.rateClass || "Standard";
      payload.riders = [];
    }

    setQuoting(true);
    setResult(null);
    try {
      const { data } = await api.post("/instabrain/quotes", {
        lead_id: lead.id,
        product: form.product,
        payload,
      });
      setResult(data);
      setVerified(true);
      await loadHistory();
      onSaved?.();
      toast.success("InstaBrain quote saved to this lead");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "InstaBrain could not produce a quote");
    } finally {
      setQuoting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="instabrain-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-[#B28A3D]" />
            InstaBrain Quote · {lead.client_name || lead.display_id}
            <span className="ml-1 rounded-full bg-[#B28A3D]/12 px-2 py-0.5 text-[10px] font-semibold text-[#7a5d27]">
              REST API
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#315A7D]/20 bg-[#315A7D]/5 p-3 text-[12px]">
          <ShieldCheck className="h-4 w-4 text-[#315A7D]" />
          {loadingStatus ? (
            <span className="text-muted-foreground">Checking InstaBrain configuration…</span>
          ) : status?.configured ? (
            <>
              <span className="font-medium text-[#2c5170]">Configured · {status.environment || "sandbox"}</span>
              {verified && <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Authenticated</span>}
            </>
          ) : (
            <span className="inline-flex items-center gap-1 text-amber-700">
              <AlertTriangle className="h-3.5 w-3.5" /> InstaBrain environment credentials are not loaded.
            </span>
          )}
          <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" onClick={verify}
            disabled={verifying || !status?.configured} data-testid="instabrain-verify">
            {verifying ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="mr-1 h-3.5 w-3.5" />}
            Verify connection
          </Button>
        </div>

        {status?.environment === "sandbox" && (
          <p className="text-[11px] text-muted-foreground">
            Sandbox mode: quote requests are tests. Nothing is sent until you press <strong>Run InstaBrain Quote</strong>.
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Product</Label>
            <select value={form.product} onChange={(e) => set("product", e.target.value)}
              className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              data-testid="instabrain-product">
              {PRODUCT_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Face amount</Label>
            <Input type="number" min="1" value={form.faceAmount} onChange={(e) => set("faceAmount", e.target.value)}
              placeholder="e.g. 25000" data-testid="instabrain-face" />
          </div>

          {isTerm && (
            <>
              <div>
                <Label className="text-xs">Term length</Label>
                <select value={form.benefitPeriod} onChange={(e) => set("benefitPeriod", e.target.value)}
                  className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option value="10">10 years</option><option value="20">20 years</option><option value="30">30 years</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Rate class</Label>
                <select value={form.rateClass} onChange={(e) => set("rateClass", e.target.value)}
                  className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option>Preferred Plus</option><option>Preferred</option><option>Standard</option><option>Standard Extra</option>
                </select>
              </div>
            </>
          )}

          <div>
            <Label className="text-xs">Age</Label>
            <Input type="number" value={form.age} onChange={(e) => set("age", e.target.value)}
              placeholder="Used if DOB is blank" data-testid="instabrain-age" />
          </div>
          <div>
            <Label className="text-xs">Date of birth</Label>
            <Input type="date" value={form.dateOfBirth} onChange={(e) => set("dateOfBirth", e.target.value)}
              data-testid="instabrain-dob" />
          </div>
          <div>
            <Label className="text-xs">Gender</Label>
            <select value={form.gender} onChange={(e) => set("gender", e.target.value)}
              className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
              {GENDERS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </div>
          <div className="flex items-end pb-1">
            <label className="flex h-9 items-center gap-2 text-sm">
              <input type="checkbox" checked={form.flagTobaccoUse}
                onChange={(e) => set("flagTobaccoUse", e.target.checked)} />
              Tobacco / nicotine use
            </label>
          </div>
          <div>
            <Label className="text-xs">State</Label>
            <Input maxLength={2} value={form.stateCode}
              onChange={(e) => set("stateCode", e.target.value.toUpperCase())} placeholder="FL" />
          </div>
          <div>
            <Label className="text-xs">ZIP code</Label>
            <Input value={form.zipCode} onChange={(e) => set("zipCode", e.target.value)} placeholder="Optional if state is set" />
          </div>
          <div>
            <Label className="text-xs">First name</Label>
            <Input value={form.firstName} onChange={(e) => set("firstName", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Last name</Label>
            <Input value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Phone</Label>
            <Input value={form.phoneNum} onChange={(e) => set("phoneNum", e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="10 digits" />
          </div>
          <div>
            <Label className="text-xs">Payment frequency</Label>
            <select value={form.paymentFreq} onChange={(e) => set("paymentFreq", e.target.value)}
              className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
              <option>Monthly</option><option>Annually</option>
            </select>
          </div>
        </div>

        {result && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="instabrain-result">
            <div className="flex items-center gap-2 font-semibold text-emerald-800">
              <CheckCircle2 className="h-4 w-4" /> Quote received and saved
            </div>
            <div className="mt-2 grid gap-2 text-sm sm:grid-cols-3">
              <div><span className="text-xs text-emerald-700">Product</span><div>{selectedProductLabel}</div></div>
              <div><span className="text-xs text-emerald-700">Quote amount</span><div className="font-semibold">{fmtMoney(result.quote_amount)}</div></div>
              <div><span className="text-xs text-emerald-700">Pipeline stage</span><div>{result.stage || "Quoted"}</div></div>
            </div>
            {result.quote_response_id && (
              <div className="mt-2 text-[11px] text-emerald-700">InstaBrain Quote Response ID: <span className="font-mono">{result.quote_response_id}</span></div>
            )}
            <div className="mt-2 text-[11px] text-emerald-700">
              Saved to this lead and Quotes &amp; Applications.
            </div>
          </div>
        )}

        {history.length > 0 && (
          <div className="rounded-lg border border-border bg-secondary/30 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold">
              <History className="h-3.5 w-3.5" /> InstaBrain quote history
            </div>
            <div className="space-y-1.5">
              {history.slice(0, 5).map((h) => (
                <div key={h.id} className="flex items-center justify-between gap-3 rounded bg-card px-2.5 py-2 text-xs">
                  <span>{h.product_label || PRODUCT_OPTIONS.find((p) => p.value === h.product)?.label || h.product}</span>
                  <span className="font-medium">{fmtMoney(h.quote_amount)}</span>
                  <span className="text-muted-foreground">{String(h.requested_at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={runQuote} disabled={quoting || !status?.configured}
            className="bg-[#B28A3D] text-[#17324D] hover:bg-[#c99b45]"
            data-testid="instabrain-run-quote">
            {quoting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Brain className="mr-1.5 h-4 w-4" />}
            {quoting ? "Running quote…" : "Run InstaBrain Quote"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
